import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { IoClose } from 'react-icons/io5';
import { MdInfoOutline } from 'react-icons/md';
import Button from 'components/Common/Button';
import Select from 'components/Common/Select';
import Input from 'components/Common/Input';
import { BusinessAddon } from '@/apis/business/addons/types';
import { useCancelBusinessAddon } from '@/apis/business/addons/hook';
import { ADDON_DEACTIVATION_REASONS } from '@/constants/cancellationReasons';
import { formatDollars } from '@/utils/helperFunctions';
import { ADDON_DRAWER_WIDTH, formatCommitmentLabel, getAddonDisplayTitle } from './addonDisplay';

interface DeactivateAddonDrawerProps {
  addon: BusinessAddon | null;
  open: boolean;
  onClose: () => void;
}

type FormValues = {
  cancellation_reason: string;
  cancellation_feedback: string;
};

type CancelMode = 'immediate' | 'end_of_billing_month';

const reasonOptions = ADDON_DEACTIVATION_REASONS.map((reason) => ({
  value: reason.value,
  label: reason.label
}));

function formatDisplayDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
}

function monthCountLabel(count: number) {
  return `${count} month(s)`;
}

function SummaryRow({
  label,
  value,
  highlight = false,
  borderBottom = true
}: {
  label: string;
  value: string;
  highlight?: boolean;
  borderBottom?: boolean;
}) {
  return (
    <Box
      display='flex'
      justifyContent='space-between'
      alignItems='flex-start'
      gap={2}
      px={highlight ? 2 : 1}
      mx={highlight ? 0 : 1}
      py={1.5}
      bgcolor={highlight ? '#F3F4F6' : 'transparent'}
      borderBottom={borderBottom ? '1px solid #ECECEC' : 'none'}
    >
      <Typography
        fontSize={14}
        fontWeight={highlight ? 400 : 500}
        color={highlight ? 'text.secondary' : 'text.primary'}
      >
        {label}
      </Typography>
      <Typography fontSize={14} fontWeight={400} textAlign='right' whiteSpace='nowrap'>
        {value}
      </Typography>
    </Box>
  );
}

export default function DeactivateAddonDrawer({ addon, open, onClose }: DeactivateAddonDrawerProps) {
  const { mutateAsync: cancelAddon, isPending } = useCancelBusinessAddon();
  const preview = addon?.subscription?.cancellation_preview;
  const commitmentActive = Boolean(preview?.commitment_active);
  const allowEndOfMonth = preview?.allow_cancel_end_of_month !== false;
  // Immediate (early termination) is only offered while a commitment is active,
  // matching the deactivate drawer designs.
  const allowImmediate = commitmentActive && preview?.allow_cancel_immediate !== false;
  const earlyFee = Number(preview?.early_termination_fee) || 0;
  const monthlyEstimate = Number(preview?.monthly_recurring_estimate) || 0;
  const remainingMonths = Number(preview?.remaining_months) || 0;
  const commitmentMonths = Number(preview?.commitment_months ?? addon?.commitment_months) || 0;
  const cycleEndsLabel = formatDisplayDate(preview?.current_cycle_ends_at);
  const commitmentEndsLabel = formatDisplayDate(preview?.commitment_ends_at || addon?.subscription?.commitment_ends_at);
  const showCommitmentEnds =
    commitmentActive && Boolean(preview?.commitment_ends_at || addon?.subscription?.commitment_ends_at);

  const defaultMode: CancelMode = allowEndOfMonth
    ? 'end_of_billing_month'
    : allowImmediate
      ? 'immediate'
      : 'end_of_billing_month';

  const [mode, setMode] = useState<CancelMode>(defaultMode);
  const [agreed, setAgreed] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: {
      cancellation_reason: '',
      cancellation_feedback: ''
    }
  });

  const reason = watch('cancellation_reason');

  useEffect(() => {
    if (!open) return;
    setMode(defaultMode);
    setAgreed(false);
    reset({ cancellation_reason: '', cancellation_feedback: '' });
  }, [open, addon?.id, defaultMode, reset]);

  const title = addon ? `Deactivate ${getAddonDisplayTitle(addon)}` : 'Deactivate Add-On';

  const infoNotice = useMemo(() => {
    if (!preview) return null;

    if (commitmentActive) {
      return `You are still inside the ${commitmentMonths} months commitment window. You can let it run to ${commitmentEndsLabel} at no extra cost, or stop it now and settle the remaining term.`;
    }

    return `Your minimum commitment is complete. The add-on stays available until ${cycleEndsLabel} — you are not billed again after that and there is no cancellation fee.`;
  }, [preview, commitmentActive, commitmentMonths, commitmentEndsLabel, cycleEndsLabel]);

  const endOfCycleLabel = commitmentActive
    ? `At the end of commitment cycle — ${commitmentEndsLabel}`
    : `At the end of this cycle — ${cycleEndsLabel}`;

  const endOfCycleHelper =
    'Recommended. Full access until then, no cancellation fee, and you can resume anytime before that date.';

  const immediateHelper = useMemo(() => {
    if (commitmentActive && earlyFee > 0) {
      return `Access ends today. The remaining ${monthCountLabel(remainingMonths)} of the commitment (${formatDollars(monthlyEstimate)} / month) are billed as a one-time fee of ${formatDollars(earlyFee)}. One-time registration / setup fees already paid are non-refundable.`;
    }
    return 'Access ends today. There is no cancellation fee.';
  }, [commitmentActive, earlyFee, remainingMonths, monthlyEstimate]);

  const agreementLabel = useMemo(() => {
    if (mode === 'immediate' && earlyFee > 0) {
      return `I understand access ends today and a ${formatDollars(earlyFee)} early termination fee will be charged.`;
    }
    if (mode === 'immediate') {
      return 'I understand this add-on becomes inactive immediately.';
    }
    if (commitmentActive) {
      return `I understand this add-on becomes inactive on ${commitmentEndsLabel}.`;
    }
    return `I understand this add-on becomes inactive on ${cycleEndsLabel}.`;
  }, [mode, earlyFee, commitmentActive, commitmentEndsLabel, cycleEndsLabel]);

  if (!addon) return null;

  const canSubmit = Boolean(reason) && agreed && (allowImmediate || allowEndOfMonth);

  const onSubmit = async (values: FormValues) => {
    await cancelAddon({
      code: addon.code,
      payload: {
        mode,
        cancellation_reason: values.cancellation_reason,
        cancellation_feedback: values.cancellation_feedback.trim()
      }
    });
    onClose();
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={() => {
        if (!isPending) onClose();
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: ADDON_DRAWER_WIDTH },
          maxWidth: '100%',
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2}
        borderBottom='1px solid #ECECEC'
      >
        <Typography variant='h6' fontWeight={500} noWrap pr={2}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size='small' aria-label='Close' disabled={isPending}>
          <IoClose size={22} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={3} py={2.5}>
        <Box border='1px solid #ECECEC' borderRadius={2} overflow='hidden' mb={1.5}>
          <SummaryRow
            label='Activated On'
            value={formatDisplayDate(addon.subscription?.activated_at || preview?.activated_at)}
          />
          <SummaryRow label='Commitment' value={formatCommitmentLabel(commitmentMonths)} />
          <SummaryRow label='Current Cycle Ends' value={cycleEndsLabel} borderBottom={showCommitmentEnds} />
          {showCommitmentEnds && (
            <SummaryRow
              label='Commitment Ends'
              value={`${commitmentEndsLabel} · ${monthCountLabel(remainingMonths)} left`}
              highlight
              borderBottom={false}
            />
          )}
        </Box>

        {infoNotice && (
          <Stack direction='row' alignItems='flex-start' gap={1} mb={2.5}>
            <Box color='text.secondary' mt={0.15} flexShrink={0} lineHeight={0}>
              <MdInfoOutline size={16} />
            </Box>
            <Typography fontSize={13} color='text.secondary' lineHeight={1.5}>
              {infoNotice}
            </Typography>
          </Stack>
        )}

        <Typography fontSize={14} fontWeight={500} mb={1}>
          When should it stop?
        </Typography>

        <RadioGroup
          value={mode}
          onChange={(e) => {
            setMode(e.target.value as CancelMode);
            setAgreed(false);
          }}
          sx={{ mb: 2.5 }}
        >
          <Box border='1px solid #ECECEC' borderRadius={2} overflow='hidden'>
            {allowEndOfMonth && (
              <Box px={1.5} py={1.25}>
                <FormControlLabel
                  value='end_of_billing_month'
                  control={<Radio size='small' />}
                  sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                  label={
                    <Box>
                      <Typography fontSize={14} fontWeight={500}>
                        {endOfCycleLabel}
                      </Typography>
                      <Typography fontSize={12} color='text.secondary' mt={0.25}>
                        {endOfCycleHelper}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}
            {allowEndOfMonth && allowImmediate && <Divider />}
            {allowImmediate && (
              <Box px={1.5} py={1.25}>
                <FormControlLabel
                  value='immediate'
                  control={<Radio size='small' />}
                  sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                  label={
                    <Box>
                      <Typography fontSize={14} fontWeight={500}>
                        {commitmentActive && earlyFee > 0 ? 'Stop immediately (early termination)' : 'Stop immediately'}
                      </Typography>
                      <Typography fontSize={12} color='text.secondary' mt={0.25}>
                        {immediateHelper}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}
          </Box>
        </RadioGroup>

        <Stack gap={2} mb={2}>
          <Select
            name='cancellation_reason'
            control={control}
            label='Reason'
            fullWidth
            sortOrder='none'
            options={reasonOptions}
            rules={{ required: true }}
          />
          <Input
            name='cancellation_feedback'
            control={control}
            label='Feedback'
            fullWidth
            multiline
            minRows={3}
            placeholder='Tell us more about your experience'
          />
        </Stack>

        <FormControlLabel
          sx={{ alignItems: 'flex-start', ml: 0 }}
          control={
            <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} size='small' sx={{ pt: 0.25 }} />
          }
          label={<Typography fontSize={13}>{agreementLabel}</Typography>}
        />
      </Box>

      <Stack direction='row' justifyContent='flex-end' gap={1.5} px={3} py={2} borderTop='1px solid #ECECEC'>
        <Button
          sx={{ bgcolor: '#fff', borderColor: '#ECECEC', color: '#8d8c8c' }}
          variant='outlined'
          color='inherit'
          onClick={onClose}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          color='error'
          disabled={!canSubmit}
          isPending={isPending}
          onClick={handleSubmit(onSubmit)}
          sx={{ textTransform: 'none' }}
        >
          Confirm Cancellation
        </Button>
      </Stack>
    </Drawer>
  );
}
